/**
 * The Leadership Brief editors' look: the same voice as the City Satisfaction
 * settings panel and the flag tree's leaf controls.
 */
import styled, { css } from 'styled-components';
import { spacing, typography, borderRadius, brandColors } from '../../design-system/tokens';

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
`;

export const fieldSurface = css`
  font-family: inherit;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
  padding: ${spacing['1.5']} ${spacing['2']};
  max-width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal}; }
  ${focusRing}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing['4']};
  min-width: 0;
`;

export const Setting = styled.div<{ $error: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing['1.5']};
  min-width: 0;
  padding-left: ${spacing['3']};
  border-left: 2px solid ${props => (props.$error
    ? props.theme?.colors?.accent?.danger || brandColors.danger
    : props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.08)')};
`;

export const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing['1']} ${spacing['2']};
`;

export const Title = styled.h4`
  margin: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

export const Arabic = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.tertiary || '#64748b'};
`;

export const Key = styled.code`
  font-family: ${typography.fontFamily.mono};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

export const State = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

export const Help = styled.p`
  margin: 0;
  max-width: 680px;
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.normal};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

export const Note = styled.p<{ $tone: 'error' | 'warning' | 'info' }>`
  margin: 0;
  max-width: 680px;
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.normal};
  color: ${props => (props.$tone === 'error'
    ? props.theme?.colors?.accent?.danger || brandColors.danger
    : props.$tone === 'warning'
      ? props.theme?.colors?.accent?.warning || brandColors.warning
      : props.theme?.colors?.text?.secondary || '#94a3b8')};
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing['1']} ${spacing['2']};
  min-width: 0;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 240px) minmax(0, 240px);
  align-items: end;
  gap: ${spacing['1.5']} ${spacing['3']};
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing['1']};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

export const Input = styled.input`
  ${fieldSurface}
  width: 100%;
`;

export const NumberInput = styled.input`
  ${fieldSurface}
  width: 110px;
`;

export const Select = styled.select`
  ${fieldSurface}
  min-width: 0;
`;

export const SmallButton = styled.button`
  ${fieldSurface}
  font-size: ${typography.fontSize.xs};
  padding: ${spacing['1']} ${spacing['2']};
  cursor: pointer;
`;

export const IconButton = styled.button`
  padding: 0 ${spacing['1']};
  font-size: ${typography.fontSize.xs};
  color: inherit;
  background: transparent;
  border: none;
  cursor: pointer;
  ${focusRing}
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export const ItemList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${spacing['1']};
  max-width: 760px;
`;

export const Item = styled.li<{ $tone: 'ok' | 'warning' | 'error' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing['1']} ${spacing['2']};
  padding: ${spacing['1']} ${spacing['2']};
  font-size: ${typography.fontSize.xs};
  border: 1px solid ${props => (props.$tone === 'error'
    ? props.theme?.colors?.accent?.danger || brandColors.danger
    : props.$tone === 'warning'
      ? props.theme?.colors?.accent?.warning || brandColors.warning
      : props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.08)')};
  border-radius: ${borderRadius.sm};
`;

export const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1 1 260px;
  min-width: 0;
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;
